import { UseQueryResult } from '@tanstack/react-query'
import dayjs, { Dayjs } from 'dayjs'
import React from 'react'
import { GetCurrentTaxYearQuery, GetRolesFromUserQuery, useGetRolesFromUserQuery } from '../generated/graphql'

import useGetCurrentTaxYear from './useGetCurrentTaxYear'
import { isByPassDisabled } from '@/config'
import { useAuth } from '@/utils/auth'

interface GlobalContextProviderProps {
  children: React.ReactNode
}

export interface GlobalContext {
  currentTaxYear: number
  currentTaxYearMinDate: Dayjs
  currentTaxYearMaxDate: Dayjs
  getCurrentTaxYearQueryResponse: UseQueryResult<GetCurrentTaxYearQuery>
  getRolesFromUserQuery: UseQueryResult<GetRolesFromUserQuery>
  hasPermission: (permission?: string | string[]) => boolean
}

const GlobalContext = React.createContext<GlobalContext>({} as GlobalContext)

export function GlobalContextProvider({ children }: GlobalContextProviderProps): JSX.Element {
  const [currentTaxYear, setCurrentTaxYear] = React.useState<number>(new Date().getFullYear())
  const { getAuthInformation } = useAuth()

  const currentTaxYearMaxDate = React.useMemo(() => {
    return getMaxDateForYear(currentTaxYear)
  }, [currentTaxYear])

  const currentTaxYearMinDate = React.useMemo(() => {
    return getMinDateForYear(currentTaxYear)
  }, [currentTaxYear])

  const getCurrentTaxYearQueryResponse = useGetCurrentTaxYear({ setCurrentTaxYear })
  const getRolesFromUserQuery = useGetRolesFromUserQuery(
    { userId: Number(getAuthInformation()?.userId) },
    {
      enabled: !!getAuthInformation() && !getAuthInformation()?.isMasterUser && !getAuthInformation()?.isMcatUser,
      cacheTime: 60,
      refetchInterval: 0,
      refetchIntervalInBackground: true,
      retry: 5,
    },
  )

  const hasPermission = React.useCallback(
    (permissions?: string | string[]): boolean => {
      if (!isByPassDisabled) return true
      if (!permissions) return true
      if (getAuthInformation()?.isMasterUser) return true
      const permissionList = Array.isArray(permissions) ? permissions : [permissions]
      return permissionList.every((permission) =>
        getRolesFromUserQuery.data?.getRolesFromUser?.some((role) =>
          role.permissions.some((p) => p.name === permission),
        ),
      )
    },
    [getAuthInformation, getRolesFromUserQuery.data?.getRolesFromUser],
  )

  const authContext = {
    currentTaxYear,
    currentTaxYearMaxDate,
    currentTaxYearMinDate,
    getCurrentTaxYearQueryResponse,
    getRolesFromUserQuery,
    hasPermission,
  }

  return <GlobalContext.Provider value={authContext}>{children}</GlobalContext.Provider>
}

function getMaxDateForYear(year: number = new Date().getFullYear()): Dayjs {
  return dayjs.utc(`${year}-12-31T23:59:59.999Z`)
}

function getMinDateForYear(year: number = new Date().getFullYear()): Dayjs {
  return dayjs.utc(`${year}-01-01T00:00:00.000Z`)
}

export default function useGlobalContext(): GlobalContext {
  return React.useContext(GlobalContext)
}
